import { useCallback, useMemo } from 'react';
import { EventCategory } from '../EventCategory.model';
import { getEventCategory } from './getEventCategory';

export function useEditEventCategory(
  allEventIds: string[],
  selectedEventIds: string[],
  setSelectedEventIds: (eventIds: string[]) => void
): {
  eventIdsSelectedDic: Record<string, boolean>;
  selectedCategoryDic: Record<string, 'some' | 'all' | 'none'>;
  eventCategories: EventCategory[];
  toggleEventId: (eventId: string) => string[];
  toggleCategory: (categoryId: string) => string[];
} {
  const eventCategories = useMemo(
    () => getEventCategory(allEventIds),
    [allEventIds]
  );
  const eventIdsSelectedDic = useMemo(() => {
    const dic: Record<string, boolean> = allEventIds.reduce((acc, eventId) => {
      acc[eventId] = selectedEventIds.includes(eventId);
      return acc;
    }, {} as Record<string, boolean>);
    return dic;
  }, [allEventIds, selectedEventIds]);

  const selectedCategoryDic = useMemo(() => {
    const dic: Record<string, 'some' | 'all' | 'none'> = {};
    eventCategories.forEach((category) => {
      const selectedCount = category.eventIds.filter(
        (eventId) => eventIdsSelectedDic[eventId]
      ).length;
      if (selectedCount === 0) {
        dic[category.id] = 'none';
      } else if (selectedCount === category.eventIds.length) {
        dic[category.id] = 'all';
      } else {
        dic[category.id] = 'some';
      }
    });
    return dic;
  }, [eventCategories, eventIdsSelectedDic]);

  // Memo this function to avoid creating a new instance on each render, which could cause unnecessary re-renders in child components that use this function as a prop.
  const toggleEventId = useCallback(
    (eventId: string) => {
      const newSelectedEventIds = allEventIds.filter((id) =>
        id === eventId
          ? !eventIdsSelectedDic[id]
          : selectedEventIds.includes(id)
      );
      setSelectedEventIds(newSelectedEventIds);
      return newSelectedEventIds;
    },
    [allEventIds, eventIdsSelectedDic, selectedEventIds, setSelectedEventIds]
  );
  const toggleCategory = useCallback(
    (categoryId: string) => {
      const category = eventCategories.find((cat) => cat.id === categoryId);
      if (!category) return selectedEventIds;

      const areAllSelected = category.eventIds.every(
        (eventId) => eventIdsSelectedDic[eventId]
      );

      const newSelectedEventIds = allEventIds.filter((id) => {
        if (category.eventIds.includes(id)) {
          return !areAllSelected;
        }
        return selectedEventIds.includes(id);
      });

      setSelectedEventIds(newSelectedEventIds);
      return newSelectedEventIds;
    },
    [
      allEventIds,
      eventCategories,
      eventIdsSelectedDic,
      selectedEventIds,
      setSelectedEventIds,
    ]
  );

  return {
    eventIdsSelectedDic,
    eventCategories,
    toggleEventId,
    toggleCategory,
    selectedCategoryDic,
  };
}
